import { ApiOptions, ClientType, CspError, Maybe, Patient } from '@csp/csp-common-model';
import {
  GENERIC_CONSENT_CACHE_KEY_PREFIX,
  getPodGenericResourceContentBlock,
  PatientClientType,
  PatientContentService,
  ttl,
} from '@csp/csp-pod-content-service';
import {
  AppPatientConsentScreenSystemTextsBlock,
  ConsentDocumentClientApplication1Type,
  ConsentDocumentContentModel,
  ConsentDocumentCountryType,
  ConsentTextContentModel,
  StepByStepTrainingContentModel,
} from '@csp/csp-pod-patient-content-definitions';
import { exhaustSwitchCase } from '@csp/csp-common-util';
import { AxiosInstance } from 'axios';
import { MemCacheService } from '@csp/csp-common-memcache';
import { CmsConsentDocument } from '../model/CmsConsentDocument';
import { ConsentArtifact, ConsentArtifactConfigV2 } from '../model/ConsentArtifact';
import { ConsentPlatform } from '../model/ConsentPlatform';
import { AppWebSystemTextIntersection, toWebAppSystemTextIntersection } from '../mapper/ConsentResponseMapper';

const toClientValue = (clientType: ConsentPlatform): ConsentDocumentClientApplication1Type => {
  switch (clientType) {
    case ConsentPlatform.APP:
    case ConsentPlatform.PATIENT_APP:
      return 'patient-app';
    case ConsentPlatform.PORTAL:
      return 'hcp-web';
    case ConsentPlatform.PATIENT_WEB:
      return 'patient-web';
    default:
      exhaustSwitchCase(clientType);
      return undefined;
  }
};

const toPatientClientType = (platform: ConsentPlatform): PatientClientType | undefined => {
  switch (platform) {
    case ConsentPlatform.APP:
    case ConsentPlatform.PATIENT_APP:
      return ClientType.PATIENT_APP;
    case ConsentPlatform.PATIENT_WEB:
      return ClientType.PATIENT_WEB;
    case ConsentPlatform.PORTAL:
      return undefined;
    default:
      return exhaustSwitchCase(platform);
  }
};

// @deprecated
const getConsentDocumentMatchingArtifact = (
  config: ConsentArtifactConfigV2,
  consentDocuments: ConsentDocumentContentModel[] = [],
): Maybe<ConsentDocumentContentModel> =>
  consentDocuments.find(
    document =>
      document.client_application?.client_value?.includes(toClientValue(config.clientType)) &&
      document.consent_type === config.cmsConsentType &&
      document.version === config.version &&
      document.countries?.country?.includes(config.country as ConsentDocumentCountryType),
  );

export interface GetConsentDocumentInput {
  config: ConsentArtifactConfigV2;
  locale: string;
  apiOptions?: ApiOptions;
}

// @deprecated
const getConsentDocument = async ({
  config,
  locale,
  apiOptions,
}: GetConsentDocumentInput): Promise<CmsConsentDocument> => {
  const consentDocuments = await PatientContentService.getConsentDocuments(locale, apiOptions);
  const systemTexts = await getAppSystemTextsForConsent(locale, apiOptions);

  return toCmsConsentDocument(config, systemTexts, consentDocuments);
};

// @deprecated
const toCmsConsentDocument = (
  config: ConsentArtifactConfigV2,
  systemTexts: AppPatientConsentScreenSystemTextsBlock,
  consentDocuments: ConsentDocumentContentModel[],
): CmsConsentDocument => {
  const document = getConsentDocumentMatchingArtifact(config, consentDocuments);

  if (!document) {
    throw CspError.badState(
      `Unable to identify Consent Document matching configuration: { clientType: ${config.clientType}, cmsConsentType: ${config.cmsConsentType}, version: ${config.version}, country: ${config.country} }`,
    );
  }

  if (!systemTexts.body?.declineAlert || !systemTexts.button?.readMore) {
    throw CspError.badState(
      `Missing content in generic stack (appPatientConsentScreen.systemTexts): "body.declineAlert" and/or "button.readMore" keys are not set`,
    );
  }

  return {
    consentType: document.consent_type,
    header: document.header,
    subHeader: document.subheader,
    bodyMd: document.body_text_md,
    declineAlertWarningText: systemTexts.body.declineAlert.text,
    readMoreButtonTextAccessibilityHint: systemTexts.button.readMore.accessibilityHint,
    readMoreButtonTextAccessibilityLabel: systemTexts.button.readMore.accessibilityLabel,
    readMoreButtonText: systemTexts.button.readMore.label,
  };
};

// @deprecated
const getCmsContentByArtifacts = async (
  artifacts: ConsentArtifact[],
  locale: string,
  apiOptions?: ApiOptions,
): Promise<CmsConsentDocument[]> =>
  await Promise.all(
    artifacts.map(
      async artifact =>
        await getConsentDocument({
          config: artifact.config,
          locale,
          apiOptions,
        }),
    ),
  );

// @deprecated
const getAppSystemTextsForConsent = async (
  locale: string,
  apiOptions?: ApiOptions,
): Promise<AppPatientConsentScreenSystemTextsBlock> =>
  await PatientContentService.getGenericBlock('appPatientConsentScreen', 'systemTexts', locale, apiOptions);

type GenericConsentContent = {
  consentDocument: ConsentDocumentContentModel[];
  consentText: ConsentTextContentModel[];
  staticContent: AppWebSystemTextIntersection;
  stepByStepTrainingContent: StepByStepTrainingContentModel[];
};

const getConsentContent = async (
  patient: Patient,
  consentPlatform: ConsentPlatform.PATIENT_WEB | ConsentPlatform.APP,
  axios: AxiosInstance,
): Promise<GenericConsentContent> => {
  const locale = patient.localization.locale;
  const cacheKey = [GENERIC_CONSENT_CACHE_KEY_PREFIX, consentPlatform, locale];

  const cached = MemCacheService.getValue<GenericConsentContent>(cacheKey);
  if (cached) {
    return cached;
  }

  const genericContentPackage = await PatientContentService.fetchGenericPackage(
    locale,
    {
      axios,
    },
    ConsentContentService.toPatientClientType(consentPlatform),
  );

  const staticContent = getPodGenericResourceContentBlock(
    genericContentPackage.resource,
    consentPlatform === ConsentPlatform.PATIENT_WEB ? 'webPatientConsentScreen' : 'appPatientConsentScreen',
    'systemTexts',
  );

  const genericConsentContent = {
    consentDocument: genericContentPackage.consent_document,
    consentText: genericContentPackage.consent_text,
    staticContent: toWebAppSystemTextIntersection(staticContent, consentPlatform),
    stepByStepTrainingContent: genericContentPackage.step_by_step_training,
  };
  MemCacheService.setValue(cacheKey, ttl, genericConsentContent);

  return genericConsentContent;
};

export const ConsentContentService = {
  // @deprecated
  getCmsContentByArtifacts,
  // @deprecated
  getConsentDocument,
  // @deprecated
  getAppSystemTextsForConsent,
  toClientValue,
  toPatientClientType,
  getConsentContent,
};
